import React from 'react';

/**
 * Utility functions for handling scroll behavior
 */

/**
 * Scrolls to the top of the page with multiple fallback methods
 * @param {Object} options - Scroll options
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: true)
 * @param {number} options.delay - Delay before scrolling in milliseconds (default: 100)
 */
export const scrollToTop = (options = {}) => {
  const { smooth = true, delay = 100 } = options;

  const performScroll = () => {
    try {
      // Method 1: Modern smooth scrolling
      if (smooth) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
        return;
      }
    } catch (error) {
      console.warn('Smooth scrolling not supported, falling back to instant scroll');
    }

    try {
      // Method 2: Instant scroll
      window.scrollTo(0, 0);
    } catch (error) {
      console.warn('window.scrollTo failed, trying alternative methods');
      
      // Method 3: Use scrollTop on document elements
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }
  };

  if (delay > 0) {
    setTimeout(performScroll, delay);
  } else {
    performScroll();
  }
};

/**
 * Scrolls to a specific element with smooth behavior
 * @param {string|HTMLElement} target - CSS selector or DOM element
 * @param {Object} options - Scroll options
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: true)
 * @param {number} options.offset - Offset from the top in pixels (default: 0)
 */
export const scrollToElement = (target, options = {}) => {
  const { smooth = true, offset = 0 } = options;
  
  const element = typeof target === 'string' ? document.querySelector(target) : target;
  
  if (!element) {
    console.warn('Target element not found:', target);
    return;
  }

  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;

  try {
    if (smooth) {
      window.scrollTo({
        top: elementPosition,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, elementPosition);
    }
  } catch (error) {
    // Fallback to instant scroll
    window.scrollTo(0, elementPosition);
  }
};

/**
 * Resets scroll position to top when component mounts
 * Useful for page components that need to start at the top
 */
export const useScrollToTop = () => {
  React.useEffect(() => {
    scrollToTop({ smooth: false, delay: 0 });
  }, []);
};
